/**
 * Motion System
 *
 * Animation tokens and utilities for fade-reveal transitions.
 * Motion should feel like "pigment lifting from a dark wash" - calm, revealing, never snappy.
 * See DESIGN.md for full motion guidelines.
 */

// Duration tokens (in milliseconds)
export const duration = {
  quick: 150,
  normal: 200,
  slow: 300,
  slower: 500,
};

export const easing = {
  reveal: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

export const staggerDelay = 50;

export const maxStaggerItems = 5;

/**
 * Get stagger delay for a given index
 * @param {number} index - Zero-based index of item in list
 * @returns {number} Delay in milliseconds
 */
export const getStaggerDelay = (index) => {
  const clampedIndex = Math.min(index, maxStaggerItems - 1);
  return clampedIndex * staggerDelay;
};

/**
 * Styled-components CSS string for fade-reveal animation
 * @param {Object} options - Animation options
 * @param {number} options.delay - Delay in milliseconds (default: 0)
 * @param {number} options.duration - Duration in milliseconds (default: normal)
 * @param {number} options.translateY - Vertical offset in pixels (default: 10)
 * @returns {string} CSS string for animation
 */
export const fadeRevealCSS = ({
  delay = 0,
  duration: dur = duration.normal,
  translateY = 10,
} = {}) => `
  @keyframes fadeReveal {
    from {
      opacity: 0;
      transform: translateY(${translateY}px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: fadeReveal ${dur}ms ${easing.reveal} ${delay}ms both;
`;

/**
 * Styled-components mixin for fade-reveal with stagger support
 * @param {number} index - Item index for stagger calculation
 * @param {Object} options - Additional animation options
 * @returns {string} CSS string
 */
export const fadeRevealStaggered = (index, options = {}) => {
  const delay = getStaggerDelay(index);
  return fadeRevealCSS({ ...options, delay });
};

export default {
  duration,
  easing,
  staggerDelay,
  maxStaggerItems,
  getStaggerDelay,
  fadeRevealCSS,
  fadeRevealStaggered,
};
